/**
 * @summary     app.getterSetter
 * @description Creates a data model that has generic getter and setter for any variable, but with added events so events can respond to updating values
 * @version     1.0
 * @file        app.getterSetter.js
 * @author      Silas Garrison (http://silasgarrison.com/)
 *
 * This source file is distributed in the hope that it will be useful, but WITHOUT ANY WARRANTY;
 * without even the implied warranty of MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.
 *
 ** Examples:

	// In this instance, you can "privatize" variables to be more class like and only make
	// certain attributes exposed via the getters and setters
	function FirstClass(){
		var foo = {
			name : "Silas",
			age : 27,
			job : "Software something"
		},
		getSet = app.getterSetter(foo);

		getSet.onGet(
			"name",
			function(attr,value){
				console.log("Thanks for asking for my name!");
			}
		);

		getSet.onBeforeSet(
			"name",
			function(attr,value){
				if(value !== "Silas"){
					alert("Sorry, I can only go by Silas");
					return false;
				}
			}
		);

		getSet.onBeforeSet(
			"age",
			function(attr,value){
				if(isNaN(value)){
					console.log("Age can only be numeric");
					return false;
				}
			}
		);

		getSet.onSet(
			"age",
			function(attr,value,origValue){
				if(origValue > value){
					alert("I'm getting younger!");
				}
				else {
					alert("I'm getting older :(")
				}
			}
		);

		return getSet;
	}

	// In this instance, we just put the getters and setters right on the object which means object.attribute can still work
	// But we can have listeners if we use object.set("attribute","value")
	var foo = {
		name : "Silas",
		age : 27,
		job : "Software something"
	},
	getSet = app.getterSetter(foo,true);

	foo.onGet(
		"name",
		function(attr,value){
			console.log("Thanks for asking for my name!");
		}
	);

	foo.onBeforeSet(
		"name",
		function(attr,value){
			if(value !== "Silas"){
				alert("Sorry, I can only go by Silas");
				return false;
			}
		}
	);

	foo.onBeforeSet(
		"age",
		function(attr,value){
			if(isNaN(value)){
				console.log("Age can only be numeric");
				return false;
			}
		}
	);

	foo.onSet(
		"age",
		function(attr,value,origValue){
			if(origValue > value){
				alert("I'm getting younger!");
			}
			else {
				alert("I'm getting older :(")
			}
		}
	);
*/

app.addFeature(
	"getterSetter",
	function(){
		"use strict";

		/*
			Name: get
			Use: INSTANCE.get()
			Args: attr String - The attribute name we're trying to get
			Description: Gets an attribute my string name from the instance object
		*/
		function get(attr){
			return this[attr];
		}

		/*
			Name: set
			Use: INSTANCE.set
			Args: attr String - The attribute name we're trying to set
					value Anything - The value we're setting this attribute to
			Description: Sets an attribute by name to be a specific value on the instance object
		*/
		function set(attr,value){
			var orig = this[attr];

			this[attr] = value;

			return orig;
		}

		/*
			Name: push
			Use: INSTANCE.push
			Args: attr String - The attribute name we're trying to append a value to
					value Anything - The value we're adding to this attribute to
			Description: Adds an element to the named attribute on the instance object
		*/
		function push(attr,value,map){
			var origArr = this[attr],
				// If they've passed in a specific value they're trying to map, assume it's a hash map
				// Otherwise, just copy the array before adding the new value
				newArr = map?(origArr || {}):([].concat(origArr || []));

			if(map){
				newArr[map] = value;
			}
			else {
				newArr.push(value);
			}
			this[attr] = newArr;

			return origArr;
		}

		/*
			Name: getterSetter
			Use: API()
			Args: scope Object - The instance object we're putting getters/setters on
			Description: Creates several functions and logical maps for handling getter/setter events for the instance object (scope) passed in
		*/
		function getterSetter(scope){
			var dispatcher = app.eventDispatcher(),
				on = dispatcher.on(),
				getters = {},
				setters = {},
				presetters = {},
				pushers = {},
				prepushers = {},
				key,
				instance = this;

			/*
				Name: onBeforeSet
				Use: INSTANCE.onBeforeSet()
				Args: attr String - The attribute name we're monitoring
						fn Function - The function to fire when the event is triggered
				Description: Registers a function to fire before a setter is ran for a specific attribute.
								If the registered function returns false, the setter will not execute
			*/
			function onBeforeSet(attr,fn){
				presetters[attr] = presetters[attr] === undefined?[]:presetters[attr];
				presetters[attr].push(fn);
			}

			/*
				Name: onSet
				Use: INSTANCE.onSet()
				Args: attr String - The attribute name we're monitoring
						fn Function - The function to fire when the event is triggered
				Description: Registers a function to fire after a setter is ran for a specific attribute
			*/
			function onSet(attr,fn){
				setters[attr] = setters[attr] === undefined?[]:setters[attr];
				setters[attr].push(fn);
			}

			/*
				Name: onBeforePush
				Use: INSTANCE.onBeforePush()
				Args: attr String - The attribute name we're monitoring
						fn Function - The function to fire when the event is triggered
				Description: Registers a function to fire before a setter is ran for a specific attribute.
								If the registered function returns false, the setter will not execute
			*/
			function onBeforePush(attr,fn){
				prepushers[attr] = prepushers[attr] === undefined?[]:prepushers[attr];
				prepushers[attr].push(fn);
			}

			/*
				Name: onPush
				Use: INSTANCE.onPush()
				Args: attr String - The attribute name we're monitoring
						fn Function - The function to fire when the event is triggered
				Description: Registers a function to fire after a setter is ran for a specific attribute
			*/
			function onPush(attr,fn){
				pushers[attr] = pushers[attr] === undefined?[]:pushers[attr];
				pushers[attr].push(fn);
			}

			/*
				Name: onGet
				Use: INSTANCE.onGet()
				Args: attr String - The attribute name we're monitoring
						fn Function - The function to fire when the event is triggered
				Description: Registers a function to fire after a getter is ran for a specific attribute
			*/
			function onGet(attr,fn){
				getters[attr] = getters[attr] === undefined?[]:getters[attr];
				getters[attr].push(fn);
			}

			/*
				Name: get
				Use: EVENT.get()
				Args: value Anything - The value of the attribute being retrieved by the getter
						attr String - The name of the attribute being retrieved by the getter
				Description: Executes any registered functions associated with the getter for the attribute specified
			*/
			on(
				"get",
				function(value,attr){
					(getters[attr] || []).concat(getters["*"] || []).forEach(
						function(fn){
							fn.apply(scope,[attr,value]);
						}
					);
				}
			);
			/*
				Name: beforeset
				Use: EVENT.beforeset()
				Args: value Anything - The value of the attribute being set by the setter
						attr String - The name of the attribute being set by the setter
				Description: Executes any registered functions associated with the setter for the attribute specified before the setter runs
			*/
			on(
				"beforeset",
				function(attr,value){
					var doContinue = true;

					(presetters[attr] || []).concat(presetters["*"] || []).forEach(
						function(fn){
							// If the function for the pre setter returns false, don't execute the setter
							if(fn.apply(scope,[attr,value]) === false){
								doContinue = false;
							}
						}
					);

					return doContinue;
				}
			);
			/*
				Name: set
				Use: EVENT.set()
				Args: value Anything - The value of the attribute being set by the setter
						attr String - The name of the attribute being set by the setter
				Description: Executes any registered functions associated with the setter for the attribute specified after the setter runs
			*/
			on(
				"set",
				function(origValue,attr,value){
					(setters[attr] || []).concat(setters["*"] || []).forEach(
						function(fn){
							fn.apply(scope,[attr,value,origValue]);
						}
					);
				}
			);
			/*
				Name: beforepush
				Use: EVENT.beforepush()
				Args: value Anything - The value of the new item being added by the pusher
						attr String - The name of the attribute being added to by the pusher
				Description: Executes any registered functions associated with the pusher for the attribute specified before the pusher runs
			*/
			on(
				"beforepush",
				function(attr,value,map){
					var doContinue = true;

					(prepushers[attr] || []).concat(prepushers["*"] || []).forEach(
						function(fn){
							// If the function for the pre setter returns false, don't execute the setter
							if(fn.apply(scope,[attr,value,map]) === false){
								doContinue = false;
							}
						}
					);

					return doContinue;
				}
			);
			/*
				Name: push
				Use: EVENT.push()
				Args: value Anything - The value of the new item being added by the pusher
						attr String - The name of the attribute being added to by the pusher
				Description: Executes any registered functions associated with the pusher for the attribute specified after the pusher runs
			*/
			on(
				"push",
				function(origValue,attr,value,map){
					(pushers[attr] || []).concat(pushers["*"] || []).forEach(
						function(fn){
							fn.apply(scope,[attr,value,origValue,map]);
						}
					);
				}
			);

			/*
				Expose these methods to the new instance so the getterSetter methods can have an external API.
				If bind is true passed into this method, then it'll bind it directly to the instance object which is the same as scope variable.
			*/
			// We have to explicitely define the registrar name because the minification renames local variables
			instance.get = dispatcher.register(get, scope, "get");
			instance.set = dispatcher.register(set, scope, "set");
			instance.push = dispatcher.register(push, scope, "push");
			instance.onBeforeSet = onBeforeSet;
			instance.onSet = onSet;
			instance.onBeforePush = onBeforePush;
			instance.onPush = onPush;
			instance.onGet = onGet;

			return instance;
		}

		return (
			function(scope,bind){
				if(bind === true){
					return getterSetter.apply(scope,[scope]);
				}
				else {
					return new getterSetter(scope);
				}
			}
		);
	}
);
